import {
  formatPacificTime,
  formatPrecipitation,
  formatWeatherValue,
  getWeatherConditionLabel,
} from "./weatherUtils";

/**
 * Hover card showing detailed OpenWeather daily timeline fields.
 *
 * @param {Object} props
 * @param {Object|null} props.dailyWeather OpenWeather daily row.
 * @param {string|null|undefined} props.status Weather row status.
 * @param {string} [props.positionClassName] Absolute-positioning classes for the hover card.
 * @returns {JSX.Element|null}
 */
export default function WeatherCard({
  dailyWeather,
  status,
  positionClassName = "right-0 origin-top-right",
}) {
  if (!dailyWeather) {
    return null;
  }

  const condition = getWeatherConditionLabel(dailyWeather);
  const sourceLabel = status === "cached" ? "Saved (Historical)" : "Live (Forecast)";
  const rain = dailyWeather.rain?.["1h"] ?? dailyWeather.rain;
  const snow = dailyWeather.snow?.["1h"] ?? dailyWeather.snow;
  const precipitation = rain ?? snow;

  return (
    <div className={`absolute top-full z-[100] mt-1 w-60 scale-0 rounded-lg border border-gray-300 bg-white p-3 text-left text-xs text-gray-700 shadow-lg transition-transform ease-in-out group-hover/weather:scale-100 ${positionClassName}`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold capitalize text-gray-800">
          {condition}
        </div>
        <div className="text-right text-[10px] font-medium uppercase tracking-wide text-gray-500">
          {sourceLabel}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        <div>Day</div>
        <div className="text-right font-medium">
          {formatWeatherValue(dailyWeather.temp?.day, "°F")}
        </div>
        <div>High / Low</div>
        <div className="text-right font-medium">
          {formatWeatherValue(dailyWeather.temp?.max, "°F")} /{" "}
          {formatWeatherValue(dailyWeather.temp?.min, "°F")}
        </div>
        <div>Feels Like</div>
        <div className="text-right font-medium">
          {formatWeatherValue(dailyWeather.feels_like?.day, "°F")}
        </div>
        <div>Humidity</div>
        <div className="text-right font-medium">
          {formatWeatherValue(dailyWeather.humidity, "%")}
        </div>
        <div>Wind</div>
        <div className="text-right font-medium">
          {formatWeatherValue(dailyWeather.wind_speed, " mph")}
        </div>
        <div>Precipitation</div>
        <div className="text-right font-medium">
          {formatPrecipitation(precipitation)}
        </div>
        {dailyWeather.snow && dailyWeather.snow > 0 && (
          <>
            <div>Snow</div>
            <div className="text-right font-medium">
              {formatWeatherValue(dailyWeather.snow, " mm/h")}
            </div>
          </>
        )}
        <div>Cloud Coverage</div>
        <div className="text-right font-medium">
          {formatWeatherValue(dailyWeather.clouds, "%")}
        </div>
        <div>Sunrise</div>
        <div className="text-right font-medium">
          {formatPacificTime(dailyWeather.sunrise)}
        </div>
        <div>Sunset</div>
        <div className="text-right font-medium">
          {formatPacificTime(dailyWeather.sunset)}
        </div>
      </div>
    </div>
  );
}
